import { Helmet } from 'react-helmet'
import { useAppContext, User } from './providers/context'
import { Params, useLocation, useNavigate } from 'react-router-dom'
import { useSmartNavigate } from './router'
import React from 'react'
import { useURLParams } from './providers/URLParams'
import { hash, isMobileSize } from './utils'
import { hasImportantHomeOnboarding } from './pages/home/homeOnboarding'
import { Logo } from './components/simple/logo'
import { storage } from './storage'

export type AppRoute = {
  path: string
  element: (user: User | undefined, params?: Params) => React.ReactNode
}

/**
 * PageTitle component that sets the page title using react-helmet.
 * @param {string} title - The title to set for the page.
 */
export const PageTitle: React.FC<{ title: string }> = ({ title }) => {
  return (
    <Helmet>
      <title>{title}</title>
    </Helmet>
  )
}

export const DefaultPageTitle: React.FC = () => {
  const { appName } = useAppContext().state
  return <PageTitle title={appName} />
}

/**
 * Logout component that handles user logout and redirects to the home page on mount.
 */
export const Logout: React.FC = () => {
  const { logout } = useAppContext()
  const navigate = useNavigate()
  React.useEffect(() => {
    logout()
    navigate('/')
  }, [])
  return null
}

/**
 * Redirect component that uses the smart navigation hook to navigate to a given path on mount.
 * @param {string} to - The path to navigate to.
 */
export const Redirect: React.FC<{ to: string }> = ({ to }) => {
  const { state } = useAppContext()
  const { user } = state
  const location = useLocation()
  const navigate = useSmartNavigate()
  React.useEffect(() => {
    if (!user && storage.session.enabled)
      storage.session.set('intendedPathForAfterLogin', location.pathname + location.search + location.hash)
    navigate(to)
  }, [])
  return null
}

export const InitialUserRedirect: React.FC = () => {
  const { state } = useAppContext()
  const user = state.user
  const smartNavigate = useSmartNavigate()
  const URLParams = useURLParams()
  const location = useLocation()

  React.useEffect(() => {
    const sessionIdentifier = hash(state.csrf + state.user?.id)

    const couldRedirectToDashboard =
      user?.type === 'teacher' &&
      user.admin > 0 &&
      storage.local.enabled &&
      storage.local.get('redirectToDashboardForSession') !== sessionIdentifier

    // we make the cookie before checking shouldRedirectToDashboard (which is if if theres no temporary reasons not to redirect)
    // we want to mark the redirect as done even if it doesnt occur in the case where we can but didnt redirect
    // this makes sure you dont get redirected upon reloading the page after completing onboarding or whatever temporary reason
    if (couldRedirectToDashboard) storage.local.set('redirectToDashboardForSession', sessionIdentifier)

    // 1. Check if the user has an initial redirect that hasn't been applied yet this tab session
    const initialRedirectIdentifier = hash(user?.initialRedirect + sessionIdentifier)
    if (
      user?.initialRedirect &&
      storage.session.enabled &&
      storage.session.get('lastInitialRedirect') !== initialRedirectIdentifier
    ) {
      storage.session.set('lastInitialRedirect', initialRedirectIdentifier)
      smartNavigate(user.initialRedirect!)
      return
    }

    // 2. Check if the user has logged in after trying to access a page they couldn't
    const intendedPath = storage.session.enabled && storage.session.get('intendedPathForAfterLogin')
    if (intendedPath) {
      storage.session.remove('intendedPathForAfterLogin')
      smartNavigate(intendedPath)
      return
    }

    // 3. See if we should send this admin to the dashboard
    const isHome = location.pathname === '/'
    if (couldRedirectToDashboard && isHome && !hasImportantHomeOnboarding(user) && !isMobileSize() && !URLParams.hasAny)
      smartNavigate('/dashboard')
  }, [user?.id])

  return null
}

export const LoadingOverlay: React.FC<{ loading: boolean; openInstantly: boolean }> = ({ loading, openInstantly }) => {
  const [loadingLong, setLoadingLong] = React.useState(false)
  React.useEffect(() => {
    if (loading) {
      const timeout = setTimeout(() => setLoadingLong(true), 1500)
      return () => clearTimeout(timeout)
    } else {
      setLoadingLong(false)
    }
  }, [loading])
  return (
    <div
      className={`duration-250 fixed inset-0 z-[9999999] flex flex-col items-center justify-center bg-white transition-opacity ease-in-out ${
        loading ? 'opacity-100' : 'pointer-events-none opacity-0'
      } ${loading && !openInstantly ? 'delay-500' : ''}`}
    >
      <Logo size="xl" />
      <div
        className={`duration-250 mt-4 h-1.5 transition-opacity duration-200 ${loadingLong ? 'animate-loadingBar opacity-100' : 'opacity-0'}`}
      >
        <div className="h-full w-full rounded-lg bg-logoLight" />
      </div>
    </div>
  )
}
